/**
 * Журнал амортизації НА (2026-08-27; 2026-08-31 — Норма #29: обидва режими
 * на InfiniteDocGrid із серверною пагінацією, рішення власника):
 *  - «за період» — відомість амортизації (метод — колонкою, підсумки по всіх
 *    рядках окремим рядком під ґрідом; друк тягне ПОВНИЙ набір);
 *  - «за документами» — документи нарахування (наші + довідки 1С, бейдж «I»,
 *    dblclick відкриває довідку) з чекбоксами відбору відомості.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ColDef } from 'ag-grid-community';
import { orgs } from '@/api/catalogs';
import {
  assetsRegistry, type DepreciationDocRow, type DepreciationStatementRow,
} from '@/api/assetsRegistry';
import { depreciationCalculation as deprCalcApi } from '@/api/depreciation';
import DatePopover from '@/components/DatePopover';
import SearchSelect from '@/components/SearchSelect';
import InfiniteDocGrid from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { useDefaultPeriod, useDefaults } from '@/store/defaults';
import TchTable from '@/shared/components/TchTable';

const fmt2 = (v: string | number | null | undefined) => v == null || v === '' || Number(v) === 0 ? ''
  : Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtD = (v: string | null | undefined) => v ? new Date(v).toLocaleDateString('uk-UA') : '';
const NUM = ['primary_cost', 'cost_start', 'cost_change', 'cost_end', 'residual_end',
  'depreciation_start', 'depreciation_period', 'depreciation_written_off', 'depreciation_end'] as const;
const HEAD = ['Первісна', 'На початок', 'Зміна', 'На кінець', 'Залишкова', 'Аморт. на початок',
  'Аморт. за період', 'Погашення', 'Аморт. на кінець'];

export default function DepreciationJournalPage() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const defaultOrgId = useDefaults((s) => s.defaultOrgId);
  // 2026-08-31 (канон DocListFilters): ЛОКАЛЬНІ дати > глобальний «період за
  // замовчуванням» з шапки; порожні поля = діє глобальний (fallback — рік).
  const gPeriod = useDefaultPeriod();
  const y = new Date().getFullYear();
  const effFrom = (v: string) => v || (gPeriod.dateFrom ? gPeriod.dateFrom.slice(0, 10) : `${y}-01-01`);
  const effTo = (v: string) => v || (gPeriod.dateTo ? gPeriod.dateTo.slice(0, 10) : new Date().toISOString().slice(0, 10));
  const [orgId, setOrgId] = useState(sp.get('organization_id') || defaultOrgId || '');
  const [kind, setKind] = useState<'' | 'FIXED_ASSET' | 'INTANGIBLE'>((sp.get('kind') as 'FIXED_ASSET') || '');
  const [dFrom, setDFrom] = useState('');
  const [dTo, setDTo] = useState('');
  const [mode, setMode] = useState<'period' | 'docs'>('period');
  const [assetId, setAssetId] = useState(sp.get('asset_id') || '');
  const [docIds, setDocIds] = useState<string[]>([]);
  // «за документами»: СВОЇ фільтри періоду/активу (2026-08-31).
  const [docFrom, setDocFrom] = useState('');
  const [docTo, setDocTo] = useState('');
  const [docAssetId, setDocAssetId] = useState('');
  // «Сформувати»: запити йдуть лише за застосованим знімком фільтрів.
  const [applied, setApplied] = useState<null | { mode: 'period' | 'docs'; dFrom: string; dTo: string; assetId: string; docFrom: string; docTo: string; docAssetId: string }>(null);
  // Чекбокси у ag-grid: renderer читає ref (кліки не потребують перемальовки).
  const docIdsRef = useRef<string[]>([]);
  docIdsRef.current = docIds;
  const docsGridRefresh = useRef<(() => void) | null>(null);
  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const assetsQ = useQuery({
    queryKey: ['assets-registry-all', orgId, kind || 'FA+IA'],
    queryFn: () => assetsRegistry.list({ organization_id: orgId, kind: kind || undefined, limit: 500 }),
    enabled: !!orgId,
  });
  const oursQ = useQuery({
    queryKey: ['depreciation-calc', orgId],
    queryFn: () => deprCalcApi.list({ organization_id: orgId, limit: 500 }),
    enabled: !!orgId && mode === 'docs',
  });
  const oursById = useMemo(() => new Map((oursQ.data?.items ?? []).map((d) => [d.id, d])), [oursQ.data]);

  // ---- «за період»: відомість із серверною пагінацією + повні підсумки ----
  const [stTotals, setStTotals] = useState<Record<string, string>>({});
  const [stRange, setStRange] = useState<{ from: string; to: string } | null>(null);
  const statementEnabled = !!orgId && !!applied
    && (applied.mode === 'period' || docIds.length > 0);
  const { datasource: stDs, total: stTotal } = useInfiniteGridDatasource<DepreciationStatementRow>(
    async (limit, offset, sort) => {
      if (!statementEnabled || !applied) return { items: [], total: 0 };
      const d = await assetsRegistry.depreciationStatement({
        sort_by: sort?.sort_by, sort_dir: sort?.sort_dir,
        organization_id: orgId, date_from: applied.dFrom, date_to: applied.dTo,
        kind: kind || undefined,
        asset_id: (applied.mode === 'docs' ? applied.docAssetId : applied.assetId) || undefined,
        document_ids: applied.mode === 'docs' ? docIds : undefined,
        limit, offset,
      });
      setStTotals(d.totals); setStRange({ from: d.date_from, to: d.date_to });
      return { items: d.rows, total: d.total_rows };
    },
    [orgId, kind, applied, docIds.join(','), statementEnabled],
  );
  const assetItems = (assetsQ.data?.items ?? [])
    .filter((a) => a.kind !== 'LOW_VALUE')
    .map((a) => ({ id: a.id, label: a.name, hint: a.inventory_number }));
  const stCols = useMemo<ColDef<DepreciationStatementRow>[]>(() => [
    { field: 'name', headerName: 'Актив', flex: 1, minWidth: 240, sortable: true,
      valueFormatter: (p) => p.data ? `${p.data.inventory_number} ${p.data.name}` : '' },
    { field: 'method_name', headerName: 'Метод', width: 150, sortable: true },
    { field: 'commissioning_date', headerName: 'Введено', width: 110, sortable: true, valueFormatter: (p) => fmtD(p.value) },
    { field: 'retirement_date', headerName: 'Знято', width: 110, sortable: true, valueFormatter: (p) => fmtD(p.value) },
    { field: 'responsible_name', headerName: 'МВО', width: 150, sortable: true },
    { field: 'department_name', headerName: 'Підрозділ', width: 140, sortable: true },
    ...NUM.map((f, i) => ({
      field: f, headerName: HEAD[i], width: 130, sortable: true,
      cellStyle: { textAlign: 'right' as const },
      valueFormatter: (p: { value?: string }) => fmt2(p.value),
    })),
  ], []);

  // ---- «за документами»: пагінований список з чекбоксами ----
  const toggleDoc = (id: string) => setDocIds((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  const { datasource: docsDs, total: docsTotal } = useInfiniteGridDatasource<DepreciationDocRow>(
    async (limit, offset, sort) => {
      if (!orgId || !applied || applied.mode !== 'docs') return { items: [], total: 0 };
      const p = await assetsRegistry.depreciationDocuments(orgId, {
        sort_by: sort?.sort_by, sort_dir: sort?.sort_dir,
        asset_id: applied.docAssetId || undefined,
        date_from: applied.docFrom || undefined, date_to: applied.docTo || undefined,
        limit, offset,
      });
      return { items: p.items, total: p.total };
    },
    [orgId, applied],
  );
  const docCols = useMemo<ColDef<DepreciationDocRow>[]>(() => [
    {
      colId: '_sel', headerName: '', width: 44, sortable: false,
      cellRenderer: (p: { data?: DepreciationDocRow }) => p.data ? (
        <input type="checkbox" defaultChecked={docIdsRef.current.includes(p.data.id)}
          onChange={() => toggleDoc(p.data!.id)}
          title="Відібрати документ у відомість (вкладка «за період» побудується за обраними)" />
      ) : null,
    },
    {
      colId: '_badge', headerName: '', width: 44, sortable: false,
      cellRenderer: (p: { data?: DepreciationDocRow }) =>
        p.data?.doc_type === 'manual_operation'
          ? <span title="Довідка з проводками 1С (закриття місяця)" style={{ color: '#2563eb', border: '1px solid var(--badge-blue-bd)', borderRadius: 4, fontSize: '0.7rem', fontWeight: 700, padding: '0 4px' }}>I</span>
          : null,
    },
    {
      colId: 'period', headerName: 'Період', width: 100, sortable: true,
      valueFormatter: (p) => p.data ? `${String(p.data.period_month).padStart(2, '0')}.${p.data.period_year}` : '',
    },
    { field: 'document_number', headerName: '№', width: 150, sortable: true },
    { field: 'document_date', headerName: 'Дата', width: 120, sortable: true, valueFormatter: (p) => fmtD(p.value) },
    {
      field: 'total', headerName: 'Сума амортизації', width: 160, sortable: true,
      cellStyle: { textAlign: 'right' }, valueFormatter: (p) => fmt2(p.value),
    },
    {
      colId: 'items_count', headerName: 'Активів', width: 100, sortable: false,
      cellStyle: { textAlign: 'right' },
      valueFormatter: (p: { data?: DepreciationDocRow }) =>
        p.data ? String(oursById.get(p.data.id)?.items_count ?? '—') : '',
    },
    {
      colId: 'skipped', headerName: 'Пропущено', width: 110, sortable: false,
      cellStyle: { textAlign: 'right' },
      valueFormatter: (p: { data?: DepreciationDocRow }) =>
        p.data ? String(oursById.get(p.data.id)?.skipped_count ?? '—') : '',
    },
  ], [oursById]);

  // ---- друк: повний набір рядків (не сторінка ґріда) ----
  const [printRows, setPrintRows] = useState<DepreciationStatementRow[] | null>(null);
  const doPrint = async () => {
    if (!applied) return;
    const d = await assetsRegistry.depreciationStatement({
      organization_id: orgId, date_from: applied.dFrom, date_to: applied.dTo, kind: kind || undefined,
      asset_id: (applied.mode === 'docs' ? applied.docAssetId : applied.assetId) || undefined,
      document_ids: applied.mode === 'docs' ? docIds : undefined,
    });
    setPrintRows(d.rows);
    setTimeout(() => { window.print(); setPrintRows(null); }, 50);
  };
  const orgName = orgList.data?.items.find((o) => o.id === orgId)?.name ?? '';

  return (
    <div className="card">
      <div className="toolbar print-hide" style={{ gap: '0.4rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Журнал амортизації необоротних активів</h2>
        <button className="primary" style={{ marginLeft: 'auto' }}
          disabled={!statementEnabled || (stTotal ?? 0) === 0} onClick={doPrint}>друкувати</button>
      </div>
      <div className="print-hide" style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '1rem' }}>
        <select value={orgId} onChange={(e) => { setOrgId(e.target.value); setDocIds([]); setAssetId(''); }} style={{ width: 190 }}>
          <option value="">— організація —</option>
          {orgList.data?.items.map((o) => <option key={o.id} value={o.id}>{o.name} — {o.code}</option>)}
        </select>
        <select value={kind} onChange={(e) => setKind(e.target.value as never)} style={{ width: 170 }}>
          <option value="">ОЗ + НМА</option>
          <option value="FIXED_ASSET">Основні засоби</option>
          <option value="INTANGIBLE">Нематеріальні активи</option>
        </select>
        <div className="seg-tabs">
          <button type="button" className={mode === 'period' ? 'active' : ''} onClick={() => setMode('period')}>за період</button>
          <button type="button" className={mode === 'docs' ? 'active' : ''} onClick={() => setMode('docs')}
            title="Відбір по конкретних документах нарахування (закриття місяця)">за документами</button>
        </div>
        {mode === 'period' && (<>
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
            title="Порожньо — діє «період за замовчуванням» з шапки">
            <DatePopover value={dFrom} onChange={setDFrom} />
            <span style={{ color: 'var(--muted)' }}>—</span>
            <DatePopover value={dTo} onChange={setDTo} />
          </span>
          <span style={{ flex: 1, minWidth: 260, display: 'flex', alignItems: 'center', gap: 2 }}>
            <span style={{ flex: 1, minWidth: 0 }}>
              <SearchSelect items={assetItems} value={assetId} onChange={setAssetId}
                placeholder="актив: усі (пошук за назвою / інв. №)" />
            </span>
            {assetId && <button type="button" style={{ padding: '0 0.4rem' }} onClick={() => setAssetId('')} title="Усі активи">×</button>}
          </span>
          <button className="primary" disabled={!orgId}
            onClick={() => setApplied({ mode: 'period', dFrom: effFrom(dFrom), dTo: effTo(dTo), assetId, docFrom, docTo, docAssetId })}>
            сформувати
          </button>
        </>)}
        {mode === 'docs' && (<>
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
            title="Період документів нарахування (порожньо — період за замовчуванням з шапки)">
            <DatePopover value={docFrom} onChange={setDocFrom} />
            <span style={{ color: 'var(--muted)' }}>—</span>
            <DatePopover value={docTo} onChange={setDocTo} />
          </span>
          <span style={{ flex: 1, minWidth: 260, display: 'flex', alignItems: 'center', gap: 2 }}>
            <span style={{ flex: 1, minWidth: 0 }}>
              <SearchSelect items={assetItems} value={docAssetId} onChange={setDocAssetId}
                placeholder="актив: усі (документи, що нараховували по ньому)" />
            </span>
            {docAssetId && <button type="button" style={{ padding: '0 0.4rem' }} onClick={() => setDocAssetId('')} title="Усі активи">×</button>}
          </span>
          <button className="primary" disabled={!orgId}
            onClick={() => { setDocIds([]); setApplied({ mode: 'docs', dFrom: effFrom(dFrom), dTo: effTo(dTo), assetId, docFrom: effFrom(docFrom), docTo: effTo(docTo), docAssetId }); }}>
            сформувати
          </button>
        </>)}
      </div>

      {!orgId && <p className="print-hide" style={{ color: 'var(--muted)' }}>Оберіть організацію.</p>}

      {orgId && mode === 'docs' && applied?.mode === 'docs' && (
        <div className="print-hide" style={{ marginBottom: '1rem' }}>
          <div style={{ fontSize: '0.82rem', color: 'var(--muted)', marginBottom: 4 }}>
            Документів: {docsTotal ?? '…'} ·{' '}
            {docIds.length > 0
              ? <>обрано {docIds.length} · <a href="#" onClick={(e) => { e.preventDefault(); setDocIds([]); docsGridRefresh.current?.(); }}>зняти всі</a></>
              : 'відмітьте документи — відомість нижче побудується за ними'}
          </div>
          <div style={{ height: 340 }}>
            <InfiniteDocGrid<DepreciationDocRow>
              datasource={docsDs}
              columnDefs={docCols}
              storageKey="depr-journal-docs"
              refreshRef={docsGridRefresh}
              onRowDoubleClicked={(r) => { if (r.doc_type === 'manual_operation') navigate(`/manual-operations/${r.id}`); }}
            />
          </div>
        </div>
      )}

      {orgId && statementEnabled && (applied?.mode === mode) && (
        <div className="print-hide">
          <div style={{ marginBottom: 6, fontSize: '0.85rem' }}>
            <b>Відомість амортизації</b>{orgName ? ` — ${orgName}` : ''} ·
            період {fmtD(stRange?.from ?? dFrom)} — {fmtD(stRange?.to ?? dTo)}
            {mode === 'docs' && docIds.length > 0 && <> · за {docIds.length} документ(ами)</>}
            {applied && (applied.mode === 'docs' ? applied.docAssetId : applied.assetId) && <> · актив: {assetItems.find((a) => a.id === (applied.mode === 'docs' ? applied.docAssetId : applied.assetId))?.label ?? ''}</>}
            {' '}· рядків: {stTotal ?? '…'}
          </div>
          <div style={{ height: 420 }}>
            <InfiniteDocGrid<DepreciationStatementRow>
              datasource={stDs}
              columnDefs={stCols}
              storageKey="depr-journal-statement"
            />
          </div>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: 6,
            fontSize: '0.82rem', fontWeight: 600 }}>
            <span>Разом:</span>
            {NUM.map((f, i) => stTotals[f] != null && Number(stTotals[f]) !== 0
              ? <span key={f} style={{ color: 'var(--muted)' }}>{HEAD[i]}: <b style={{ color: 'inherit' }}>{fmt2(stTotals[f])}</b></span>
              : null)}
          </div>
        </div>
      )}
      {orgId && !applied && (
        <p className="print-hide" style={{ color: 'var(--muted)' }}>Задайте фільтри і натисніть «сформувати».</p>
      )}
      {orgId && mode === 'docs' && applied?.mode === 'docs' && docIds.length === 0 && (
        <p className="print-hide" style={{ color: 'var(--muted)' }}>Оберіть документи нарахування — зʼявиться відомість.</p>
      )}

      {/* Друкована форма — повний набір рядків, тягнеться при кліку «друкувати». */}
      {printRows && (
        <div className="print-page" style={{ fontSize: '0.78rem' }}>
          <div style={{ marginBottom: 6 }}>
            <b>Відомість амортизації необоротних активів</b>{orgName ? ` — ${orgName}` : ''}<br />
            Період: {fmtD(stRange?.from ?? dFrom)} — {fmtD(stRange?.to ?? dTo)}
          </div>
          <TchTable style={{ minWidth: 1100 }}>
            <thead>
              <tr>
                <th>Актив</th><th>Метод</th><th>Введено</th><th>Знято</th><th>МВО</th><th>Підрозділ</th>
                {HEAD.map((h) => <th key={h} style={{ textAlign: 'right' }}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {printRows.map((r) => (
                <tr key={r.id}>
                  <td>{r.inventory_number} {r.name}</td>
                  <td>{r.method_name ?? ''}</td>
                  <td>{fmtD(r.commissioning_date)}</td>
                  <td>{fmtD(r.retirement_date)}</td>
                  <td>{r.responsible_name ?? ''}</td>
                  <td>{r.department_name ?? ''}</td>
                  {NUM.map((f) => <td key={f} style={{ textAlign: 'right' }}>{fmt2(r[f])}</td>)}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: 700 }}>
                <td colSpan={6}>Разом</td>
                {NUM.map((f) => <td key={f} style={{ textAlign: 'right' }}>{fmt2(stTotals[f])}</td>)}
              </tr>
            </tfoot>
          </TchTable>
        </div>
      )}
    </div>
  );
}
